/**
 * Utility functions for working with ConsentManager
 * Enhanced version with support for consent categories, local persistence,
 * and improved error handling
 */

// Tipos y enums
export enum ConsentCategory {
  NECESSARY = 'necessary',
  ANALYTICS = 'analytics',
  MARKETING = 'marketing',
  PREFERENCES = 'preferences',
  SOCIAL_MEDIA = 'social_media',
}

export interface ConsentStatus {
  advertising: boolean;
  analytics: boolean;
  functional: boolean;
}

export interface ExtendedConsentStatus {
  hasConsent: boolean;
  categories: {
    [key in ConsentCategory]?: boolean;
  };
  lastUpdated: number;
}

export type ConsentCallback = (status: ConsentStatus) => void;
export type ExtendedConsentCallback = (status: ExtendedConsentStatus) => void;

// Definiciones globales
declare global {
  interface Window {
    _cmp?: {
      show: () => void;
      consent: {
        getConsentStatus: () => boolean;
        onChange: (callback: (consent: boolean) => void) => void;
        getConsentStatusForCategory?: (category: ConsentCategory) => boolean;
        getConsentStatusForService?: (service: string) => boolean;
        getConsentStatusForVendor?: (vendor: string) => boolean;
      };
    };
  }
}

const CONSENT_KEY = 'user_consent_preferences';

// Forzar que el banner aparezca al inicio
const initialConsent: ConsentStatus = {
  advertising: false,
  analytics: false,
  functional: false,
};

// Implementación principal del ConsentManager
class ConsentManager {
  private static instance: ConsentManager;
  private consentStatus: ConsentStatus = { ...initialConsent };
  private extendedStatus: ExtendedConsentStatus = {
    hasConsent: false,
    categories: {
      [ConsentCategory.NECESSARY]: true,
      [ConsentCategory.ANALYTICS]: false,
      [ConsentCategory.MARKETING]: false,
      [ConsentCategory.PREFERENCES]: false,
      [ConsentCategory.SOCIAL_MEDIA]: false,
    },
    lastUpdated: Date.now(),
  };
  private listeners: ConsentCallback[] = [];
  private extendedListeners: ExtendedConsentCallback[] = [];

  private constructor() {
    try {
      this.loadStoredConsent();
    } catch (error) {
      console.error('Error initializing consent manager:', error);
      // Fallback to default values if initialization fails
      this.consentStatus = { ...initialConsent };
      this.extendedStatus = {
        hasConsent: false,
        categories: {
          [ConsentCategory.NECESSARY]: true,
          [ConsentCategory.ANALYTICS]: false,
          [ConsentCategory.MARKETING]: false,
          [ConsentCategory.PREFERENCES]: false,
          [ConsentCategory.SOCIAL_MEDIA]: false,
        },
        lastUpdated: Date.now(),
      };
    }
  }

  public static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  private loadStoredConsent(): void {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) {
        // Si no hay consentimiento almacenado, usar valores iniciales
        this.consentStatus = { ...initialConsent };
        this.extendedStatus = {
          hasConsent: false,
          categories: {
            [ConsentCategory.NECESSARY]: true,
            [ConsentCategory.ANALYTICS]: false,
            [ConsentCategory.MARKETING]: false,
            [ConsentCategory.PREFERENCES]: false,
          },
          lastUpdated: Date.now(),
        };
      } else {
        const parsed = JSON.parse(stored);
        this.consentStatus = {
          ...initialConsent,
          ...(parsed.basic || {}),
        };
        this.extendedStatus = {
          ...this.extendedStatus,
          ...(parsed.extended || {}),
          categories: {
            [ConsentCategory.NECESSARY]: true, // Siempre necesario
            [ConsentCategory.ANALYTICS]: this.consentStatus.analytics,
            [ConsentCategory.MARKETING]: this.consentStatus.advertising,
            [ConsentCategory.PREFERENCES]: this.consentStatus.functional,
          },
        };
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Error loading consent:', error);
      this.resetConsent();
    }
  }

  private saveConsent(): void {
    try {
      const dataToStore = {
        basic: this.consentStatus,
        extended: this.extendedStatus,
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  }

  public updateConsent(newStatus: Partial<ConsentStatus>): void {
    this.consentStatus = {
      ...this.consentStatus,
      ...newStatus,
    };

    // Actualizar el estado extendido
    this.extendedStatus = {
      hasConsent: Object.values(this.consentStatus).some(v => v),
      categories: {
        [ConsentCategory.NECESSARY]: true, // Siempre necesario
        [ConsentCategory.ANALYTICS]: this.consentStatus.analytics,
        [ConsentCategory.MARKETING]: this.consentStatus.advertising,
        [ConsentCategory.PREFERENCES]: this.consentStatus.functional,
      },
      lastUpdated: Date.now(),
    };

    this.saveConsent();
    this.notifyListeners();
  }

  public getConsentStatus(): ConsentStatus {
    return { ...this.consentStatus };
  }

  public getExtendedStatus(): ExtendedConsentStatus {
    return { ...this.extendedStatus };
  }

  public onConsentChange(callback: ConsentCallback): () => void {
    this.listeners.push(callback);
    // Llamar inmediatamente con el estado actual
    callback(this.consentStatus);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  public onExtendedConsentChange(callback: ExtendedConsentCallback): () => void {
    this.extendedListeners.push(callback);
    // Llamar inmediatamente con el estado actual
    callback(this.extendedStatus);
    return () => {
      this.extendedListeners = this.extendedListeners.filter(listener => listener !== callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.consentStatus));
    this.extendedListeners.forEach(listener => listener(this.extendedStatus));
  }

  public hasAnalyticsConsent(): boolean {
    return this.consentStatus.analytics;
  }

  public hasAdvertisingConsent(): boolean {
    return this.consentStatus.advertising;
  }

  public hasFunctionalConsent(): boolean {
    return this.consentStatus.functional;
  }

  public hasConsentForCategory(category: ConsentCategory): boolean {
    return this.extendedStatus.categories[category] || false;
  }

  public getLastConsentUpdate(): Date {
    return new Date(this.extendedStatus.lastUpdated);
  }

  public resetConsent(): void {
    this.consentStatus = { ...initialConsent };
    this.extendedStatus = {
      hasConsent: false,
      categories: {},
      lastUpdated: Date.now(),
    };
    this.saveConsent();
    this.notifyListeners();
  }
}

// Instancia singleton del ConsentManager
export const consentManager = ConsentManager.getInstance();

// Funciones de utilidad para el CMP externo
export const isConsentManagerLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window._cmp;
};

export const showConsentManager = (): void => {
  if (isConsentManagerLoaded()) {
    window._cmp?.show();
  } else {
    console.warn('ConsentManager is not loaded yet');
  }
};

export const getExternalConsentStatus = (): boolean => {
  try {
    if (isConsentManagerLoaded()) {
      return window._cmp?.consent.getConsentStatus() || false;
    }

    // Fallback: check for consent cookie
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'consent' && value === 'true') {
          return true;
        }
      }
    }
  } catch {
    // Silenciar errores al obtener el estado de consentimiento
  }
  return false;
};

export const onExternalConsentChange = (callback: (consent: boolean) => void): (() => void) => {
  if (isConsentManagerLoaded()) {
    try {
      window._cmp?.consent.onChange(callback);
    } catch {
      // Silenciar errores al registrar el callback
    }
    return () => {};
  }

  // Intentar registrar cuando el CMP se cargue
  const checkInterval = setInterval(() => {
    if (isConsentManagerLoaded()) {
      clearInterval(checkInterval);
      try {
        window._cmp?.consent.onChange(callback);
      } catch {
        // Silenciar errores al registrar el callback
      }
    }
  }, 500);

  // Limpiar después de 10 segundos si no se carga
  const timeoutId = setTimeout(() => {
    clearInterval(checkInterval);
    try {
      callback(false);
    } catch {
      // Silenciar errores al llamar al callback
    }
  }, 10000);

  return () => {
    clearInterval(checkInterval);
    clearTimeout(timeoutId);
  };
};
